<script setup>
import { ref, computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf } from '@vuelidate/validators';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import {
  appendSignature,
  removeSignature,
  getEffectiveChannelType,
  stripUnsupportedMarkdown,
} from 'dashboard/helper/editorHelper';
import {
  buildContactableInboxesList,
  prepareNewMessagePayload,
  prepareWhatsAppMessagePayload,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper.js';

import { useCopilotReply } from 'dashboard/composables/useCopilotReply';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';

import ContactSelector from './ContactSelector.vue';
import InboxSelector from './InboxSelector.vue';
import EmailOptions from './EmailOptions.vue';
import MessageEditor from './MessageEditor.vue';
import ActionButtons from './ActionButtons.vue';
import InboxEmptyState from './InboxEmptyState.vue';
import AttachmentPreviews from './AttachmentPreviews.vue';
import CopilotReplyBottomPanel from 'dashboard/components/widgets/WootWriter/CopilotReplyBottomPanel.vue';

const props = defineProps({
  contacts: { type: Array, default: () => [] },
  contactId: { type: String, default: null },
  selectedContact: { type: Object, default: null },
  targetInbox: { type: Object, default: null },
  currentUser: { type: Object, default: null },
  isCreatingContact: { type: Boolean, default: false },
  isFetchingInboxes: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
  isDirectUploadsEnabled: { type: Boolean, default: false },
  contactConversationsUiFlags: { type: Object, default: null },
  contactsUiFlags: { type: Object, default: null },
  messageSignature: { type: String, default: '' },
  sendWithSignature: { type: Boolean, default: false },
  formState: { type: Object, required: true },
});

const emit = defineEmits([
  'searchContacts',
  'resetContactSearch',
  'discard',
  'updateSelectedContact',
  'updateTargetInbox',
  'clearSelectedContact',
  'createConversation',
]);

const formatStepNumber = step => String(step).padStart(2, '0');

const DEFAULT_FORMATTING = 'Context::Default';

const copilot = useCopilotReply();

const showContactsDropdown = ref(false);
const showInboxesDropdown = ref(false);
const showCcEmailsDropdown = ref(false);
const showBccEmailsDropdown = ref(false);

const isCreating = computed(() => props.contactConversationsUiFlags.isCreating);

const state = props.formState || {
  message: '',
  subject: '',
  ccEmails: '',
  bccEmails: '',
  attachedFiles: [],
};

const inboxTypes = computed(() => ({
  isEmail: props.targetInbox?.channelType === INBOX_TYPES.EMAIL,
  isTwilio: props.targetInbox?.channelType === INBOX_TYPES.TWILIO,
  isWhatsapp: props.targetInbox?.channelType === INBOX_TYPES.WHATSAPP,
  isWebWidget: props.targetInbox?.channelType === INBOX_TYPES.WEB,
  isApi: props.targetInbox?.channelType === INBOX_TYPES.API,
  isEmailOrWebWidget:
    props.targetInbox?.channelType === INBOX_TYPES.EMAIL ||
    props.targetInbox?.channelType === INBOX_TYPES.WEB,
  isTwilioSMS:
    props.targetInbox?.channelType === INBOX_TYPES.TWILIO &&
    props.targetInbox?.medium === 'sms',
  isTwilioWhatsapp:
    props.targetInbox?.channelType === INBOX_TYPES.TWILIO &&
    props.targetInbox?.medium === 'whatsapp',
}));

const whatsappMessageTemplates = computed(() =>
  Object.keys(props.targetInbox?.messageTemplates || {}).length
    ? props.targetInbox.messageTemplates
    : []
);

const inboxChannelType = computed(() => props.targetInbox?.channelType || '');

const inboxMedium = computed(() => props.targetInbox?.medium || '');

const effectiveChannelType = computed(() =>
  getEffectiveChannelType(inboxChannelType.value, inboxMedium.value)
);

const validationRules = computed(() => ({
  selectedContact: { required },
  targetInbox: { required },
  message: { required: requiredIf(!inboxTypes.value.isWhatsapp) },
  subject: { required: requiredIf(inboxTypes.value.isEmail) },
}));

const v$ = useVuelidate(validationRules, {
  selectedContact: computed(() => props.selectedContact),
  targetInbox: computed(() => props.targetInbox),
  message: computed(() => state.message),
  subject: computed(() => state.subject),
});

const validationStates = computed(() => ({
  isContactInvalid:
    v$.value.selectedContact.$dirty && v$.value.selectedContact.$invalid,
  isInboxInvalid: v$.value.targetInbox.$dirty && v$.value.targetInbox.$invalid,
  isSubjectInvalid: v$.value.subject.$dirty && v$.value.subject.$invalid,
  isMessageInvalid: v$.value.message.$dirty && v$.value.message.$invalid,
}));

const newMessagePayload = () => {
  const { message, subject, ccEmails, bccEmails, attachedFiles } = state;
  return prepareNewMessagePayload({
    targetInbox: props.targetInbox,
    selectedContact: props.selectedContact,
    message,
    subject,
    ccEmails,
    bccEmails,
    currentUser: props.currentUser,
    attachedFiles,
    directUploadsEnabled: props.isDirectUploadsEnabled,
  });
};

const contactableInboxesList = computed(() => {
  return buildContactableInboxesList(props.selectedContact?.contactInboxes);
});

const showNoInboxAlert = computed(() => {
  return (
    props.selectedContact &&
    contactableInboxesList.value.length === 0 &&
    !props.contactsUiFlags.isFetchingInboxes &&
    !props.isFetchingInboxes
  );
});

const isAnyDropdownActive = computed(() => {
  return (
    showContactsDropdown.value ||
    showInboxesDropdown.value ||
    showCcEmailsDropdown.value ||
    showBccEmailsDropdown.value
  );
});

const handleContactSearch = value => {
  showContactsDropdown.value = value.trim().length > 1;
  emit('searchContacts', value);
};

const handleDropdownUpdate = (type, value) => {
  if (type === 'cc') {
    showCcEmailsDropdown.value = value;
  } else if (type === 'bcc') {
    showBccEmailsDropdown.value = value;
  } else {
    showContactsDropdown.value = value;
  }
};

const searchCcEmails = value => {
  showBccEmailsDropdown.value = false;
  emit('resetContactSearch');
  showCcEmailsDropdown.value = value.trim().length >= 2;
  emit('searchContacts', value);
};

const searchBccEmails = value => {
  showCcEmailsDropdown.value = false;
  emit('resetContactSearch');
  showBccEmailsDropdown.value = value.trim().length >= 2;
  emit('searchContacts', value);
};

const setSelectedContact = async ({ value, action, ...rest }) => {
  v$.value.$reset();
  emit('updateSelectedContact', { value, action, ...rest });
  showContactsDropdown.value = false;
  showInboxesDropdown.value = true;
};

const stripMessageFormatting = channelType => {
  if (!state.message || !channelType) return;

  state.message = stripUnsupportedMarkdown(state.message, channelType, false);
};

const handleInboxAction = ({ value, action, channelType, medium, ...rest }) => {
  v$.value.$reset();
  copilot.reset(false);

  // Strip unsupported formatting when changing the target inbox
  if (channelType) {
    const newChannelType = getEffectiveChannelType(channelType, medium);
    stripMessageFormatting(newChannelType);
  }

  emit('updateTargetInbox', { ...rest, channelType, medium });
  showInboxesDropdown.value = false;
  state.attachedFiles = [];
};

const removeSignatureFromMessage = () => {
  // Always remove the signature from message content when inbox/contact is removed
  // to ensure no leftover signature content remains
  if (props.messageSignature) {
    state.message = removeSignature(
      state.message,
      props.messageSignature,
      effectiveChannelType.value
    );
  }
};

const removeTargetInbox = value => {
  v$.value.$reset();
  copilot.reset(false);
  removeSignatureFromMessage();

  stripMessageFormatting(DEFAULT_FORMATTING);

  emit('updateTargetInbox', value);
  state.attachedFiles = [];
};

const clearSelectedContact = () => {
  copilot.reset(false);
  removeSignatureFromMessage();
  emit('clearSelectedContact');
  state.message = '';
  state.attachedFiles = [];
};

const onClickInsertEmoji = emoji => {
  state.message += emoji;
};

const handleAddSignature = signature => {
  state.message = appendSignature(
    state.message,
    signature,
    effectiveChannelType.value
  );
};

const handleRemoveSignature = signature => {
  state.message = removeSignature(
    state.message,
    signature,
    effectiveChannelType.value
  );
};

const handleAttachFile = files => {
  state.attachedFiles = files;
};

const clearForm = () => {
  copilot.reset(false);
  Object.assign(state, {
    message: '',
    subject: '',
    ccEmails: '',
    bccEmails: '',
    attachedFiles: [],
  });
  v$.value.$reset();
};

const handleSendMessage = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  try {
    const success = await emit('createConversation', {
      payload: newMessagePayload(),
      isFromWhatsApp: false,
    });
    if (success) {
      clearForm();
    }
  } catch (error) {
    // Form will not be cleared if conversation creation fails
  }
};

const handleSendWhatsappMessage = async ({ message, templateParams }) => {
  const whatsappMessagePayload = prepareWhatsAppMessagePayload({
    targetInbox: props.targetInbox,
    selectedContact: props.selectedContact,
    message,
    templateParams,
    currentUser: props.currentUser,
  });
  await emit('createConversation', {
    payload: whatsappMessagePayload,
    isFromWhatsApp: true,
  });
};

const handleSendTwilioMessage = async ({ message, templateParams }) => {
  const twilioMessagePayload = prepareWhatsAppMessagePayload({
    targetInbox: props.targetInbox,
    selectedContact: props.selectedContact,
    message,
    templateParams,
    currentUser: props.currentUser,
  });
  await emit('createConversation', {
    payload: twilioMessagePayload,
    isFromWhatsApp: true,
  });
};

const shouldShowMessageEditor = computed(() => {
  return (
    !inboxTypes.value.isWhatsapp &&
    !showNoInboxAlert.value &&
    !inboxTypes.value.isTwilioWhatsapp
  );
});

const isCopilotActive = computed(() => copilot.isActive?.value ?? false);

const isContactStepDone = computed(() => !!props.selectedContact);
const isInboxStepDone = computed(() => !!props.targetInbox);
const isMessageStepDone = computed(
  () => (state.message || '').trim().length > 0
);

const completedSteps = computed(() => {
  return [
    isContactStepDone.value,
    isInboxStepDone.value,
    isMessageStepDone.value,
  ].filter(Boolean).length;
});

const onSubmitCopilotReply = () => {
  const acceptedMessage = copilot.accept();
  state.message = acceptedMessage;
};

useKeyboardEvents({
  '$mod+Enter': {
    action: () => {
      if (isCopilotActive.value && !copilot.isButtonDisabled.value) {
        onSubmitCopilotReply();
      }
    },
    allowOnFocusedInput: true,
  },
});
</script>

<template>
  <div
    class="compose-form w-[42rem] overflow-visible transition-all duration-300 ease-in-out top-full flex flex-col bg-n-alpha-3 border border-n-strong shadow-sm backdrop-blur-[100px] rounded-xl min-w-0 max-h-[calc(50vh-4rem)]"
  >
    <!-- Заголовок: pt-6 pb-5 → pt-4 pb-3, текст 2.8rem → 1.85rem -->
    <div class="px-6 pt-4 pb-3 border-b border-n-strong">
      <p class="text-xs tracking-[0.14em] uppercase text-n-brand mb-1">
        {{ $t('CONTACT_PANEL.NEW_MESSAGE') }}
      </p>
      <h2
        class="text-[1.85rem] leading-none font-semibold text-n-slate-12 mb-0"
      >
        {{ $t('NEW_CONVERSATION.TITLE') }}
      </h2>
    </div>

    <div class="flex-1 overflow-y-auto">
      <!-- Секция To/Via: py-5 → py-3, gap-6 → gap-4 -->
      <div class="grid grid-cols-2 px-6 py-3 gap-4 border-b border-n-strong">
        <div class="compose-step-field border-r border-n-strong pr-4">
          <!-- Номер шага: 1.7rem → 1.1rem -->
          <p class="text-n-brand text-[1.1rem] font-semibold mb-0.5">
            {{ formatStepNumber(1) }}
          </p>
          <p class="text-n-slate-11 mb-1.5 text-sm">
            {{ $t('COMPOSE_NEW_CONVERSATION.FORM.CONTACT_SELECTOR.LABEL') }}
          </p>
          <ContactSelector
            :contacts="contacts"
            :selected-contact="selectedContact"
            :show-contacts-dropdown="showContactsDropdown"
            :is-loading="isLoading"
            :is-creating-contact="isCreatingContact"
            :contact-id="contactId"
            :contactable-inboxes-list="contactableInboxesList"
            :show-inboxes-dropdown="showInboxesDropdown"
            :has-errors="validationStates.isContactInvalid"
            @search-contacts="handleContactSearch"
            @set-selected-contact="setSelectedContact"
            @clear-selected-contact="clearSelectedContact"
            @update-dropdown="handleDropdownUpdate"
          />
        </div>

        <div class="compose-step-field">
          <p class="text-n-brand text-[1.1rem] font-semibold mb-0.5">
            {{ formatStepNumber(2) }}
          </p>
          <p class="text-n-slate-11 mb-1.5 text-sm">
            {{ $t('COMPOSE_NEW_CONVERSATION.FORM.INBOX_SELECTOR.LABEL') }}
          </p>
          <InboxEmptyState v-if="showNoInboxAlert" />
          <InboxSelector
            v-else
            :target-inbox="targetInbox"
            :selected-contact="selectedContact"
            :show-inboxes-dropdown="showInboxesDropdown"
            :contactable-inboxes-list="contactableInboxesList"
            :has-errors="validationStates.isInboxInvalid"
            :is-fetching-inboxes="isFetchingInboxes"
            @update-inbox="removeTargetInbox"
            @toggle-dropdown="showInboxesDropdown = $event"
            @handle-inbox-action="handleInboxAction"
          />
        </div>
      </div>

      <!-- Секция Message: py-5 → py-3, mb-3 → mb-2 -->
      <div class="px-6 py-3">
        <div class="mb-2">
          <p
            class="text-n-slate-10 tracking-[0.14em] uppercase font-medium mb-0 text-xs"
          >
            {{ formatStepNumber(3) }}
            {{ $t('NEW_CONVERSATION.FORM.MESSAGE.LABEL') }}
          </p>
        </div>

        <EmailOptions
          v-if="inboxTypes.isEmail"
          v-model:cc-emails="state.ccEmails"
          v-model:bcc-emails="state.bccEmails"
          v-model:subject="state.subject"
          :contacts="contacts"
          :show-cc-emails-dropdown="showCcEmailsDropdown"
          :show-bcc-emails-dropdown="showBccEmailsDropdown"
          :is-loading="isLoading"
          :has-errors="validationStates.isSubjectInvalid"
          @search-cc-emails="searchCcEmails"
          @search-bcc-emails="searchBccEmails"
          @update-dropdown="handleDropdownUpdate"
        />

        <MessageEditor
          v-if="shouldShowMessageEditor"
          v-model="state.message"
          :message-signature="messageSignature"
          :send-with-signature="sendWithSignature"
          :has-errors="validationStates.isMessageInvalid"
          :channel-type="inboxChannelType"
          :medium="targetInbox?.medium || ''"
          :copilot="copilot"
        />

        <AttachmentPreviews
          v-if="state.attachedFiles.length > 0"
          :attachments="state.attachedFiles"
          @update:attachments="state.attachedFiles = $event"
        />
      </div>
    </div>

    <CopilotReplyBottomPanel
      v-if="isCopilotActive"
      :is-generating-content="copilot.isButtonDisabled.value"
      class="h-[2.5rem] !px-3 !py-1.5"
      @submit="onSubmitCopilotReply"
      @cancel="copilot.reset"
    />
    <ActionButtons
      v-else
      :attached-files="state.attachedFiles"
      :is-whatsapp-inbox="inboxTypes.isWhatsapp"
      :is-email-or-web-widget-inbox="inboxTypes.isEmailOrWebWidget"
      :is-twilio-sms-inbox="inboxTypes.isTwilioSMS"
      :is-twilio-whats-app-inbox="inboxTypes.isTwilioWhatsapp"
      :message-templates="whatsappMessageTemplates"
      :channel-type="inboxChannelType"
      :is-loading="isCreating"
      :disable-send-button="isCreating"
      :has-selected-inbox="!!targetInbox"
      :inbox-id="targetInbox?.id"
      :has-no-inbox="showNoInboxAlert"
      :is-dropdown-active="isAnyDropdownActive"
      :message-signature="messageSignature"
      @insert-emoji="onClickInsertEmoji"
      @add-signature="handleAddSignature"
      @remove-signature="handleRemoveSignature"
      @attach-file="handleAttachFile"
      @discard="$emit('discard')"
      @send-message="handleSendMessage"
      @send-whatsapp-message="handleSendWhatsappMessage"
      @send-twilio-message="handleSendTwilioMessage"
    />

    <!-- Прогресс-бар: pb-2.5 → pb-1.5, gap-3 → gap-2 -->
    <div class="grid grid-cols-3 gap-2 px-6 pb-1.5">
      <span
        class="h-[2px] rounded-full transition-colors duration-200"
        :class="completedSteps >= 1 ? 'bg-n-brand' : 'bg-n-strong'"
      />
      <span
        class="h-[2px] rounded-full transition-colors duration-200"
        :class="completedSteps >= 2 ? 'bg-n-brand' : 'bg-n-strong'"
      />
      <span
        class="h-[2px] rounded-full transition-colors duration-200"
        :class="completedSteps >= 3 ? 'bg-n-brand' : 'bg-n-strong'"
      />
    </div>
  </div>
</template>

<style scoped>
.compose-step-field :deep(.relative.flex-1.px-4.py-3.overflow-y-visible),
.compose-step-field
  :deep(.flex.items-center.flex-1.w-full.gap-3.px-4.py-3.overflow-y-visible) {
  padding: 0;
}

.compose-step-field
  :deep(label.text-sm.font-medium.text-n-slate-11.whitespace-nowrap),
.compose-step-field
  :deep(label.mb-0\.5.text-sm.font-medium.text-n-slate-11.whitespace-nowrap) {
  display: none;
}

/* 3.25rem → 2.17rem (÷1.5) */
.compose-step-field :deep(.flex.items-baseline.w-full.gap-3.min-h-7),
.compose-step-field :deep(.relative.flex.items-center.h-7) {
  min-height: 2.17rem;
}

.compose-form :deep(.ProseMirror-woot-style) {
  min-height: 7rem !important;
  max-height: 7.5rem !important;
}
</style>
